/**
 * Client-side image compression for offline photo uploads.
 *
 * Field photos are often 10-20 MB straight from the camera. Compressing
 * before storing in IndexedDB and uploading over Starlink/cell saves real
 * bytes (and time) at minimal visual cost.
 *
 * Returns the original blob unchanged if it isn't an image or compression
 * wouldn't reduce the size.
 */

const MAX_DIMENSION = 2048;
const JPEG_QUALITY = 0.8;

export interface CompressedImage {
  blob: Blob;
  content_type: string;
  width: number;
  height: number;
  originalSize: number;
  compressedSize: number;
}

export interface CompressOptions {
  maxDimension?: number;
  quality?: number;
}

export async function compressImageBlob(
  blob: Blob,
  options: CompressOptions = {}
): Promise<CompressedImage> {
  const maxDim = options.maxDimension ?? MAX_DIMENSION;
  const quality = options.quality ?? JPEG_QUALITY;
  const fallback = (b: Blob, w = 0, h = 0): CompressedImage => ({
    blob: b,
    content_type: b.type,
    width: w,
    height: h,
    originalSize: blob.size,
    compressedSize: b.size,
  });

  if (!blob.type.startsWith('image/')) {
    return fallback(blob);
  }

  let img: HTMLImageElement;
  try {
    img = await loadImageFromBlob(blob);
  } catch {
    return fallback(blob);
  }

  const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
  const targetWidth = Math.round(img.width * scale);
  const targetHeight = Math.round(img.height * scale);

  const canvas = document.createElement('canvas');
  canvas.width = targetWidth;
  canvas.height = targetHeight;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    return fallback(blob, img.width, img.height);
  }
  ctx.drawImage(img, 0, 0, targetWidth, targetHeight);

  const compressed = await new Promise<Blob | null>((resolve) => {
    canvas.toBlob(resolve, 'image/jpeg', quality);
  });

  if (!compressed || compressed.size >= blob.size) {
    return fallback(blob, img.width, img.height);
  }

  return {
    blob: compressed,
    content_type: 'image/jpeg',
    width: targetWidth,
    height: targetHeight,
    originalSize: blob.size,
    compressedSize: compressed.size,
  };
}

export function rewriteFilenameForJpeg(filename: string): string {
  const lastDot = filename.lastIndexOf('.');
  if (lastDot < 0) return `${filename}.jpg`;
  return `${filename.slice(0, lastDot)}.jpg`;
}

function loadImageFromBlob(blob: Blob): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to decode image'));
    };
    img.src = url;
  });
}
